import type { FieldValues } from 'react-hook-form';
import { z } from 'zod';

import {
  type AppropriatenessTest,
  appropriatenessTestAverageComplexTransactionValueSchema,
  appropriatenessTestComplexClassificationReasonSchema,
  appropriatenessTestComplexTransactionCountSchema,
  appropriatenessTestFuturesKnowledgeStatementsSchema,
  appropriatenessTestInvestingExperienceLevelSchema,
  appropriatenessTestLeverageAttitudeSchema,
  appropriatenessTestMaintenanceMarginBreachSchema,
  appropriatenessTestMarginRequirementAtLeverageSchema,
  appropriatenessTestMarginWipeoutPriceMoveSchema,
  appropriatenessTestOvernightLossReactionSchema,
  appropriatenessTestRiskAttitudeSchema,
  appropriatenessTestTotalLossImpactSchema,
} from '@/features/futures/schemas/appropriateness-test';
import type { FormStep as BaseFormStep, FormStepComponent as BaseFormStepComponent } from '@/types/components';
import { FuturesAppropriatenessTestIntro, FuturesAppropriatenessTestIntroLegend } from './appropriateness-test-intro';
import { FuturesAppropriatenessTestSummary, FuturesAppropriatenessTestSummaryLegend } from './appropriateness-test-summary';
import {
  FuturesAppropriatenessTestFieldsetAverageComplexTransactionValue,
  FuturesAppropriatenessTestFieldsetAverageComplexTransactionValueLegend,
} from './fieldsets/average-complex-transaction-value';
import {
  FuturesAppropriatenessTestFieldsetComplexClassificationReason,
  FuturesAppropriatenessTestFieldsetComplexClassificationReasonLegend,
} from './fieldsets/complex-classification-reason';
import { FuturesAppropriatenessTestFieldsetComplexTransactionCount, FuturesAppropriatenessTestFieldsetComplexTransactionCountLegend } from './fieldsets/complex-transaction-count';
import {
  FuturesAppropriatenessTestFieldsetInvestingExperienceLevel,
  FuturesAppropriatenessTestFieldsetInvestingExperienceLevelLegend,
} from './fieldsets/investing-experience-level';
import { FuturesAppropriatenessTestFieldsetFuturesKnowledgeStatements, FuturesAppropriatenessTestFieldsetFuturesKnowledgeStatementsLegend } from './fieldsets/knowledge-statements';
import { FuturesAppropriatenessTestFieldsetLeverageAttitude, FuturesAppropriatenessTestFieldsetLeverageAttitudeLegend } from './fieldsets/leverage-attitude';
import { FuturesAppropriatenessTestFieldsetMaintenanceMarginBreach, FuturesAppropriatenessTestFieldsetMaintenanceMarginBreachLegend } from './fieldsets/maintenance-margin-breach';
import {
  FuturesAppropriatenessTestFieldsetMarginRequirementAtLeverage,
  FuturesAppropriatenessTestFieldsetMarginRequirementAtLeverageLegend,
} from './fieldsets/margin-requirement-at-leverage';
import { FuturesAppropriatenessTestFieldsetMarginWipeoutPriceMove, FuturesAppropriatenessTestFieldsetMarginWipeoutPriceMoveLegend } from './fieldsets/margin-wipeout-price-move';
import { FuturesAppropriatenessTestFieldsetOvernightLossReaction, FuturesAppropriatenessTestFieldsetOvernightLossReactionLegend } from './fieldsets/overnight-loss-reaction';
import { FuturesAppropriatenessTestFieldsetRiskAttitude, FuturesAppropriatenessTestFieldsetRiskAttitudeLegend } from './fieldsets/risk-attitude';
import { FuturesAppropriatenessTestFieldsetTotalLossImpact, FuturesAppropriatenessTestFieldsetTotalLossImpactLegend } from './fieldsets/total-loss-impact';

type FormStepComponent<T extends FieldValues = FieldValues> = BaseFormStepComponent<T> & {
  validateOnNavigation?: boolean;
};

type FormStep<T extends FieldValues = FieldValues> = BaseFormStep<T> & {
  Component: FormStepComponent<T>;
};

type FormAncillaryStepKey = 'intro';

export type FormStepKey = keyof AppropriatenessTest | FormAncillaryStepKey;

const NOOP_SCHEMA = z.object({});

export const FORM_STEPS = {
  intro: {
    Component: FuturesAppropriatenessTestIntro,
    Legend: FuturesAppropriatenessTestIntroLegend,
    schema: NOOP_SCHEMA,
  },
  futuresKnowledgeStatements: {
    Component: FuturesAppropriatenessTestFieldsetFuturesKnowledgeStatements,
    Legend: FuturesAppropriatenessTestFieldsetFuturesKnowledgeStatementsLegend,
    schema: appropriatenessTestFuturesKnowledgeStatementsSchema,
  },
  marginRequirementAtLeverage: {
    Component: FuturesAppropriatenessTestFieldsetMarginRequirementAtLeverage,
    Legend: FuturesAppropriatenessTestFieldsetMarginRequirementAtLeverageLegend,
    schema: appropriatenessTestMarginRequirementAtLeverageSchema,
  },
  maintenanceMarginBreach: {
    Component: FuturesAppropriatenessTestFieldsetMaintenanceMarginBreach,
    Legend: FuturesAppropriatenessTestFieldsetMaintenanceMarginBreachLegend,
    schema: appropriatenessTestMaintenanceMarginBreachSchema,
  },
  marginWipeoutPriceMove: {
    Component: FuturesAppropriatenessTestFieldsetMarginWipeoutPriceMove,
    Legend: FuturesAppropriatenessTestFieldsetMarginWipeoutPriceMoveLegend,
    schema: appropriatenessTestMarginWipeoutPriceMoveSchema,
  },
  complexClassificationReason: {
    Component: FuturesAppropriatenessTestFieldsetComplexClassificationReason,
    Legend: FuturesAppropriatenessTestFieldsetComplexClassificationReasonLegend,
    schema: appropriatenessTestComplexClassificationReasonSchema,
  },
  complexTransactionCount: {
    Component: FuturesAppropriatenessTestFieldsetComplexTransactionCount,
    Legend: FuturesAppropriatenessTestFieldsetComplexTransactionCountLegend,
    schema: appropriatenessTestComplexTransactionCountSchema,
  },
  averageComplexTransactionValue: {
    Component: FuturesAppropriatenessTestFieldsetAverageComplexTransactionValue,
    Legend: FuturesAppropriatenessTestFieldsetAverageComplexTransactionValueLegend,
    schema: appropriatenessTestAverageComplexTransactionValueSchema,
  },
  investingExperienceLevel: {
    Component: FuturesAppropriatenessTestFieldsetInvestingExperienceLevel,
    Legend: FuturesAppropriatenessTestFieldsetInvestingExperienceLevelLegend,
    schema: appropriatenessTestInvestingExperienceLevelSchema,
  },
  riskAttitude: {
    Component: FuturesAppropriatenessTestFieldsetRiskAttitude,
    Legend: FuturesAppropriatenessTestFieldsetRiskAttitudeLegend,
    schema: appropriatenessTestRiskAttitudeSchema,
  },
  overnightLossReaction: {
    Component: FuturesAppropriatenessTestFieldsetOvernightLossReaction,
    Legend: FuturesAppropriatenessTestFieldsetOvernightLossReactionLegend,
    schema: appropriatenessTestOvernightLossReactionSchema,
  },
  totalLossImpact: {
    Component: FuturesAppropriatenessTestFieldsetTotalLossImpact,
    Legend: FuturesAppropriatenessTestFieldsetTotalLossImpactLegend,
    schema: appropriatenessTestTotalLossImpactSchema,
  },
  leverageAttitude: {
    Component: FuturesAppropriatenessTestFieldsetLeverageAttitude,
    Legend: FuturesAppropriatenessTestFieldsetLeverageAttitudeLegend,
    schema: appropriatenessTestLeverageAttitudeSchema,
  },
  confirmation: {
    Component: FuturesAppropriatenessTestSummary,
    Legend: FuturesAppropriatenessTestSummaryLegend,
    schema: NOOP_SCHEMA,
  },
} satisfies Record<FormStepKey, FormStep<AppropriatenessTest>>;

export const STEPS = Object.values(FORM_STEPS).map((step) => step.Component);
export const STEP_KEYS = Object.keys(FORM_STEPS) as FormStepKey[];
export const STEP_LEGENDS = Object.values(FORM_STEPS).map((step) => step.Legend);
export const STEP_SCHEMAS = Object.values(FORM_STEPS).map((step) => step.schema);
